import { getAssessmentMeta, getSeverityColor, formatDate, getRiskStyles } from './dashboardDisplayUtils';
import ChatSummaryCard from './ChatSummaryCard';
import DashboardAssessmentCards from './DashboardAssessmentCards';
import MindVaultAlertsBanner from './MindVaultAlertsBanner';
import ConsultationOverviewSection from './ConsultationOverviewSection';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
  IconButton,
  TextField,
  Dialog,
  DialogContent,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import AssignmentIcon from '@mui/icons-material/Assignment';
import TimelineIcon from '@mui/icons-material/Timeline';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { supabase } from '../../../supabaseClient.js';
import { CLIENT_CHAT_SESSION_TYPES } from '../../../utils/chatSessionsQuery.js';
import WellnessOverviewSection from './WellnessOverviewSection.js';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CloseIcon from '@mui/icons-material/Close';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp'; 
import { scoreLabel, clampScore, getHrStatus, getSpo2Status, getBpStatus, getHrvStatus, getSiStatus, isSiCritical, getRrStatus, toNumOrNull } from './mindvaultUtils.js';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, ReferenceArea } from 'recharts'; 
import { ConstructionOutlined } from '@mui/icons-material';
import {
  getConnectionStatus,
  ENTITLEMENT,
  RESTRICTED_ACCESS_LABEL,
} from '../../../utils/scribeConnection.js';
import BuyAccessCodeButton from '../../../components/BuyAccessCodeButton.js';
import CreditConnectionPurchase from '../../../components/CreditConnectionPurchase.js';
import { PHASE45_COMMS_ENABLED } from '../../../constants/phase45Comms.js';

const parseArrayString = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value !== 'string') return [];
  const trimmed = value.trim();
  if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
    // Try JSON first
    try {
      const jsonLike = trimmed.replace(/'/g, '"');
      const parsed = JSON.parse(jsonLike);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      // Fallback: split by commas
      return trimmed
        .slice(1, -1)
        .split(',')
        .map((item) => item.trim().replace(/^['"]|['"]$/g, ''))
        .filter(Boolean);
    }
  }
  return [trimmed];
};

const normalizeDigits = (value) => String(value ?? '').replace(/\D/g, '');

const lockedCardSx = {
    p: 4,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    width: '100%',
    maxWidth: 520,
    boxSizing: 'border-box',
    backgroundColor: '#FFFFFF',
    borderRadius: 3,
    border: '1px solid #E3E9EE',
    boxShadow: '0 12px 32px rgba(20,35,75,0.14)',
};

const UNLOCK_TITLE = 'Unlock with MindVault';
const UNLOCK_INTRO = "Connect with this client's MindVault to see their chat summaries, wellness scans (including HRV) and PHQ-9 / GAD-7 trends between sessions.";

// Static stand-in for the MindVault sections behind the locked card. It never
// renders client data, so blurring it cannot leak anything.
const LockedMindVaultPreview = () => (
    <Grid container spacing={2} aria-hidden="true">
        {['Chat summary', 'PHQ-9 and GAD-7', 'Wellness overview'].map((title, index) => (
            <Grid key={title} size={{ xs: 12, md: index === 2 ? 12 : 6 }}>
                <Paper elevation={0} sx={{ p: 2.5, minHeight: 200, borderRadius: 2, border: '1px solid #E3E9EE' }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 17, mb: 2 }}>{title}</Typography>
                    {[90, 70, 80].map((width) => (
                        <Box key={width} sx={{ height: 12, width: `${width}%`, bgcolor: '#E3E9EE', borderRadius: 1, mb: 1.5 }} />
                    ))}
                </Paper>
            </Grid>
        ))}
    </Grid>
);




const VitalsDashboard = ({ patient, onLinkStatusChange, currentUserId, mode = 'Mindvault', view = "", record = null, currentOrg = null }) => {
    // const isCorporateVitalsMode = mode === 'Corporates' && view === 'Vitals';
    const isCorporateVitalsMode = false;
    const [loading, setLoading] = useState(false);
    const [isLinked, setIsLinked] = useState(false);
    const [isAccessDenied, setIsAccessDenied] = useState(false);
    // Access denied *while paid*: the therapist has a booking or an access code
    // and is only missing the client's sharing consent.
    const [isAwaitingConsent, setIsAwaitingConsent] = useState(false);
    // D-021: the client's last MindVault booking is older than 30 days, so the
    // free booking connection lapsed — distinct copy from "never connected".
    const [isBookingLapsed, setIsBookingLapsed] = useState(false);
    const [connectionEntitlement, setConnectionEntitlement] = useState(null);
    const [mindvaultUserId, setMindvaultUserId] = useState(null);
    const [connectionRefreshVersion, setConnectionRefreshVersion] = useState(0);
    const [chatSessions, setChatSessions] = useState([]);
    const [selectedSessionId, setSelectedSessionId] = useState(null);
    const [conversationMap, setConversationMap] = useState({});
    const [loadingConversation, setLoadingConversation] = useState(false);
    const [openTranscriptSessionId, setOpenTranscriptSessionId] = useState(null);
    const [scans, setScans] = useState([]);
    const [scansLoading, setScansLoading] = useState(false);

    const [assessments, setAssessments] = useState([]);
    const [assessmentsLoading, setAssessmentsLoading] = useState(false);
    const [selectedAssessmentType, setSelectedAssessmentType] = useState(null);

    const [chatRange, setChatRange] = useState('week');
    const [chatCustomFrom, setChatCustomFrom] = useState('');
    const [chatCustomTo, setChatCustomTo] = useState('');
    const [chatAppliedFrom, setChatAppliedFrom] = useState('');
    const [chatAppliedTo, setChatAppliedTo] = useState('');

    const chatSummariesRef = useRef(null);
    const wellnessRef = useRef(null);
    const assessmentsRef = useRef(null);

    const latestSession = useMemo(
    () => (chatSessions.length > 0 ? chatSessions[0] : null),
    [chatSessions]
    );

    const latestScan = useMemo(() => (scans.length > 0 ? scans[0] : null), [scans]);

    const activeRiskFlags = useMemo(() => {
    const all = (chatSessions || []).flatMap((s) =>
            Array.isArray(s.risk_signals) ? s.risk_signals : []
        );
        return Array.from(new Set(all.filter(Boolean)));
        }, [chatSessions]);

    const latestRiskSignals = Array.isArray(latestSession?.risk_signals)
    ? latestSession.risk_signals
    : [];


    const filteredChatSessions = useMemo(() => {
        const sessions = chatSessions || [];
        if (chatRange === 'last') return sessions.slice(0, 1);

        const now = new Date();
        let from = null;
        let to = null;

        if (chatRange === 'week') {
            from = new Date();
            from.setDate(from.getDate() - 7);
            from.setHours(0, 0, 0, 0);
        } else if (chatRange === 'month') {
            from = new Date();
            from.setDate(from.getDate() - 30);
            from.setHours(0, 0, 0, 0);
        } else if (chatRange === 'custom') {
            if (chatAppliedFrom) from = new Date(chatAppliedFrom);
            if (chatAppliedTo) {
            to = new Date(chatAppliedTo);
            to.setHours(23, 59, 59, 999);
            }
        }

        return sessions.filter((s) => {
            const d = new Date(s.created_at);
            if (from && d < from) return false;
            if (to && d > to) return false;
            return true;
        });
    }, [chatSessions, chatRange, chatAppliedFrom, chatAppliedTo]);

    useEffect(() => {
        if (!filteredChatSessions.length) {
            setSelectedSessionId(null);
        } else if (!filteredChatSessions.find((s) => s.session_id === selectedSessionId)) {
            setSelectedSessionId(filteredChatSessions[0].session_id);
        }
    }, [filteredChatSessions, selectedSessionId]);


    const latestScanAverage = useMemo(() => {
        if (!latestScan) return null;
        const values = [
            latestScan.activity,
            latestScan.sleep,
            latestScan.equilibrium,
            latestScan.metabolism,
            latestScan.health,
            latestScan.relaxation,
        ]
            .map((v) => Number(v))
            .filter((n) => !Number.isNaN(n));
        if (!values.length) return null;
        return values.reduce((a, b) => a + b, 0) / values.length;
    }, [latestScan]);

    const vitalSummary = useMemo(() => {
        if (!latestScan) return null;

        const hr = toNumOrNull(latestScan.heart_rate);
        const spo2 = toNumOrNull(latestScan.oxygen_saturation);
        const rr = toNumOrNull(latestScan.respiration_rate);
        const hrv = toNumOrNull(latestScan.hr_variability);
        const sbp = toNumOrNull(latestScan.sbp);
        const dbp = toNumOrNull(latestScan.dbp);

        const vitals = [
            { name: 'Heart Rate', status: getHrStatus(hr) },
            { name: 'Blood Pressure', status: getBpStatus(sbp, dbp) },
            { name: 'Blood Oxygen', status: getSpo2Status(spo2) },
            { name: 'Respiratory Rate', status: getRrStatus(rr) },
            { name: 'HRV', status: getHrvStatus(hrv) },
        ];

        const normal = vitals.filter((v) => v.status === 'normal').map((v) => v.name);
        const abnormal = vitals.filter((v) => v.status === 'abnormal').map((v) => v.name);
        const critical = vitals.filter((v) => v.status === 'critical').map((v) => v.name);

        const stressLevel = getSiStatus(latestScan.stress_index);

        const buildList = (items) => (items.length === 1 ? items[0] : items.join(', '));

        // Neutral wording (as in HealthSeer): "needs attention" / "worth monitoring"
        // instead of "critical" / "abnormal".
        let statusSummary = '';
        if (critical.length > 0) {
            const verb = critical.length === 1 ? 'needs' : 'need';
            statusSummary = `${buildList(critical)} ${verb} attention.`;
            if (abnormal.length > 0 && normal.length > 0) {
                statusSummary += ` ${buildList(abnormal)} ${abnormal.length === 1 ? 'is' : 'are'} worth monitoring, and the rest of the vital signs are normal.`;
            } else if (abnormal.length > 0) {
                statusSummary += ` ${buildList(abnormal)} ${abnormal.length === 1 ? 'is' : 'are'} worth monitoring.`;
            } else if (normal.length > 0) {
                statusSummary += ` The rest of the vital signs are normal.`;
            }
        } else if (abnormal.length > 0) {
            const verb = abnormal.length === 1 ? 'is' : 'are';
            statusSummary =
                normal.length > 0
                    ? `${buildList(abnormal)} ${verb} worth monitoring, and the rest of the vital signs are normal.`
                    : `${buildList(abnormal)} ${verb} worth monitoring.`;
        } else {
            statusSummary = 'All vital signs are normal.';
        }

        return { normal, abnormal, critical, stressLevel, stressCritical: isSiCritical(stressLevel), statusSummary };
    }, [latestScan]);


    useEffect(() => {
        if (!isCorporateVitalsMode) return;

        const parseArrayString = (value) => {
            if (!value) return [];
            if (Array.isArray(value)) return value;
            if (typeof value !== 'string') return [];
            const trimmed = value.trim();
            if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
            try {
                const jsonLike = trimmed.replace(/'/g, '"');
                const parsed = JSON.parse(jsonLike);
                return Array.isArray(parsed) ? parsed : [];
            } catch {
                return trimmed.slice(1, -1).split(',').map((s) => s.trim()).filter(Boolean);
            }
            }
            return [trimmed];
        };

        const loadCorporateRecordData = async () => {
        if (!currentOrg?.id || !record?.id) return;

        setLoading(true);
        setScansLoading(true);
        try {
            const isSession = !!record.session_id;
            const idParts = String(record.id).split(':');
            const source = idParts[0];
            const rawId = idParts[1];

            let scansQuery = supabase
            .from('scan_history')
            .select('id, created_at, activity, sleep, equilibrium, metabolism, health, relaxation, heart_rate, sbp, dbp, blood_pressure, oxygen_saturation, respiration_rate, stress_index, hr_variability, session_id')
            .eq('team_id', currentOrg.id)
            .is('user_id', null)
            .order('created_at', { ascending: false });

            let chatsQuery = supabase
            .from('chat_sessions')
            .select('id, created_at, session_id, emotions, key_themes, session_summary, risk_level, risk_signals, risk_confidence')
            .eq('team_id', currentOrg.id)
            .is('user_id', null)
            .eq('is_deleted', false)
            .order('created_at', { ascending: false });

            if (isSession) {
            scansQuery = scansQuery.eq('session_id', record.session_id);
            chatsQuery = chatsQuery.eq('session_id', record.session_id);
            } else if (source === 'scan' && rawId) {
            scansQuery = scansQuery.eq('id', rawId);
            chatsQuery = chatsQuery.limit(0);
            } else if (source === 'chat' && rawId) {
            chatsQuery = chatsQuery.eq('id', rawId);
            scansQuery = scansQuery.limit(0);
            }

            const [{ data: scansData, error: scansErr }, { data: chatData, error: chatErr }] = await Promise.all([
            scansQuery,
            chatsQuery,
            ]);

            if (scansErr) throw scansErr;
            if (chatErr) throw chatErr;

            const decryptMaybe = async (encryptedValue) => {
            if (!encryptedValue) return '';
            const { data, error } = await supabase.rpc('decrypt_text', { encrypted_data: encryptedValue });
            if (error || data == null) {
                // fallback when value is already plain text / not encrypted
                return typeof encryptedValue === 'string' ? encryptedValue : '';
            }
            return data;
            };

            const decryptedChats = await Promise.all(
            (chatData || []).map(async (session) => {
                const [emotionsRaw, themesRaw, summaryRaw] = await Promise.all([
                decryptMaybe(session.emotions),
                decryptMaybe(session.key_themes),
                decryptMaybe(session.session_summary),
                ]);

                let riskSignals = session.risk_signals;
                if (typeof riskSignals === 'string') {
                try {
                    riskSignals = JSON.parse(riskSignals);
                } catch {
                    riskSignals = [];
                }
                }

                return {
                ...session,
                emotions: parseArrayString(emotionsRaw),
                key_themes: parseArrayString(themesRaw),
                session_summary: summaryRaw || '',
                risk_signals: Array.isArray(riskSignals) ? riskSignals : [],
                };
            })
            );

            setScans(scansData || []);
            setChatSessions(decryptedChats);

            setIsLinked(true);
            setMindvaultUserId(null);
            onLinkStatusChange?.(true);
        } catch (e) {
            console.error('Error loading corporate vitals record data:', e);
            setScans([]);
            setChatSessions([]);
        } finally {
            setScansLoading(false);
            setLoading(false);
        }
        };

        loadCorporateRecordData();
    }, [isCorporateVitalsMode, currentOrg?.id, record?.id, record?.session_id, onLinkStatusChange]);


    useEffect(() => {
        if (isCorporateVitalsMode) return;

        const fetchScans = async () => {
            if (!mindvaultUserId) {
            setScans([]);
            return;
            }
            setScansLoading(true);
            try {
            const { data, error } = await supabase
                .from('scan_history')
                .select(
                'id, created_at, activity, sleep, equilibrium, metabolism, health, relaxation, heart_rate, sbp, dbp, blood_pressure, oxygen_saturation, respiration_rate, stress_index, hr_variability'
                )
                .eq('user_id', mindvaultUserId)
                .order('created_at', { ascending: false });

            if (error) {
                console.error('Error fetching scan_history:', error);
                setScans([]);
                return;
            }
            setScans(data || []);
            } catch (err) {
            console.error('Error loading scan_history:', err);
            setScans([]);
            } finally {
            setScansLoading(false);
            }
        };

        const fetchAssessments = async () => {
            if (!mindvaultUserId) {
                setAssessments([]);
                return;
            }
            setAssessmentsLoading(true);
            try {
                const { data, error } = await supabase
                    .from('mental_health_responses')
                    .select('id, assessment_type, total_score, severity_level, completed_at')
                    .eq('user_id', mindvaultUserId)
                    .order('completed_at', { ascending: false });

                if (error) {
                    console.error('Error fetching mental_health_responses:', error);
                    setAssessments([]);
                } else {
                    setAssessments(data || []);
                }
            } catch (err) {
                console.error('Error loading assessments:', err);
                setAssessments([]);
            } finally {
                setAssessmentsLoading(false);
            }
        };
        fetchScans();
        fetchAssessments();
    }, [isCorporateVitalsMode, mindvaultUserId]);

    useEffect(() => {
        if (isCorporateVitalsMode) return;

        const syncLink = async () => {
            if (!patient?.email) {
            setIsLinked(false);
            setIsAccessDenied(false);
            setIsAwaitingConsent(false);
            setConnectionEntitlement(null);
            setMindvaultUserId(null);
            onLinkStatusChange?.(false);
            return;
            }

            setLoading(true);
            try {
            // Connected = (booking OR access code) AND consent — server-side rule.
            // hasMvAccount without connected => access denied; awaitingConsent
            // splits that into "paid, client hasn't shared" vs "nothing paid".
            const { connected, mindvaultAccountId, hasMvAccount, awaitingConsent, entitlement, bookingLapsed } =
                await getConnectionStatus(currentUserId, patient.email);

            setConnectionEntitlement(entitlement);

            if (!connected) {
                setIsLinked(false);
                setIsAccessDenied(hasMvAccount);
                setIsAwaitingConsent(awaitingConsent);
                setIsBookingLapsed(bookingLapsed);
                setMindvaultUserId(null);
                onLinkStatusChange?.(false);
                return;
            }

            setIsAccessDenied(false);
            setIsAwaitingConsent(false);
            setIsBookingLapsed(false);
            setIsLinked(true);
            setMindvaultUserId(mindvaultAccountId);
            onLinkStatusChange?.(true);
            } catch (err) {
            console.error('Error linking mindvault account:', err);
            setIsLinked(false);
            setIsAwaitingConsent(false);
            setMindvaultUserId(null);
            onLinkStatusChange?.(false);
            } finally {
            setLoading(false);
            }
        };

        syncLink();
    }, [isCorporateVitalsMode, patient?.email, currentUserId, onLinkStatusChange, connectionRefreshVersion]);

    useEffect(() => {
        if (isCorporateVitalsMode) return;

        const fetchSessions = async () => {
            if (!mindvaultUserId) {
            setChatSessions([]);
            return;
            }

            setLoading(true);
            try {
            const { data, error } = await supabase
            .from('chat_sessions')
            .select(
                'id, created_at, user_id, session_id, emotions, key_themes, session_summary, risk_level, risk_signals, risk_confidence'
            )
            .eq('user_id', mindvaultUserId)
            .eq('is_deleted', false)
            .or(CLIENT_CHAT_SESSION_TYPES)
            .order('created_at', { ascending: false });

            if (error) {
                console.error('Error fetching chat_sessions:', error);
                setChatSessions([]);
                return;
            }

            const decrypted = await Promise.all(
                (data || []).map(async (session) => {
                const [emotionsRes, themesRes, summaryRes] = await Promise.all([
                    supabase.rpc('decrypt_text', { encrypted_data: session.emotions }),
                    supabase.rpc('decrypt_text', { encrypted_data: session.key_themes }),
                    supabase.rpc('decrypt_text', { encrypted_data: session.session_summary }),
                ]);

                return {
                    ...session,
                    emotions: parseArrayString(emotionsRes?.data),
                    key_themes: parseArrayString(themesRes?.data),
                    session_summary: summaryRes?.data || '',
                };
                })
            );

            setChatSessions(decrypted);
            if (decrypted.length > 0) {
                setSelectedSessionId(decrypted[0].session_id);
            }
            } catch (err) {
            console.error('Error decrypting chat_sessions:', err);
            setChatSessions([]);
            } finally {
            setLoading(false);
            }
        };

        fetchSessions();
    }, [isCorporateVitalsMode, mindvaultUserId]);


    useEffect(() => {
    if (!isCorporateVitalsMode) return;
    if (!selectedSessionId || conversationMap[selectedSessionId]) return;

    const fetchConversationVitals = async () => {
        setLoadingConversation(true);
        try {
        const { data, error } = await supabase
            .rpc('get_decrypted_conversations', { p_session_ids: [selectedSessionId] });

        if (error) {
            console.error('Error fetching corporate transcript:', error);
            setConversationMap((prev) => ({ ...prev, [selectedSessionId]: [] }));
            return;
        }

        const firstRow = data && data[0];
        const conversation = firstRow?.conversation || [];

        setConversationMap((prev) => ({
            ...prev,
            [selectedSessionId]: Array.isArray(conversation) ? conversation : [],
        }));
        } catch (err) {
        console.error('Error loading corporate transcript:', err);
        setConversationMap((prev) => ({ ...prev, [selectedSessionId]: [] }));
        } finally {
        setLoadingConversation(false);
        }
    };

    fetchConversationVitals();
    }, [isCorporateVitalsMode, selectedSessionId, conversationMap]);
    

    useEffect(() => {
        if (isCorporateVitalsMode) return;

        const fetchConversation = async () => {
            if (!selectedSessionId || conversationMap[selectedSessionId] || !mindvaultUserId) return;

            setLoadingConversation(true);
            try {
            const { data, error } = await supabase
                .rpc('get_decrypted_conversations', { p_session_ids: [selectedSessionId] });

            if (error) {
                console.error('Error fetching chat_messages_sessions:', error);
                return;
            }

            const firstRow = data && data[0];
            const conversation = firstRow?.conversation || [];

            setConversationMap((prev) => ({ ...prev, [selectedSessionId]: Array.isArray(conversation) ? conversation : [] }));
            } catch (err) {
            console.error('Error loading conversation:', err);
            } finally {
            setLoadingConversation(false);
            }
        };

        fetchConversation();
    }, [isCorporateVitalsMode, selectedSessionId, conversationMap, mindvaultUserId]);

    const assessmentGroups = useMemo(() => {
        const groups = {};
        assessments.forEach(a => {
            if (!groups[a.assessment_type]) groups[a.assessment_type] = [];
            groups[a.assessment_type].push(a);
        });
        
        // Sort each group by date descending (already done by SQL, but safe to ensure)
        Object.keys(groups).forEach(key => {
            groups[key].sort((a, b) => new Date(b.completed_at) - new Date(a.completed_at));
        });
        
        return groups;
    }, [assessments]);

    const scrollTo = (ref) => {
    if (!ref?.current) return;
    ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const selectedConversation = conversationMap[selectedSessionId] || [];

    const openSession = useMemo(
    () => chatSessions.find((s) => s.session_id === openTranscriptSessionId) || null,
    [chatSessions, openTranscriptSessionId]
    );

    const lockedState = (() => {
    if (loading && !isLinked && !isAccessDenied && !isAwaitingConsent) {
    return (
        <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
        </Box>
    );
    }

    // No email on file is a different situation from "we looked and found nothing":
    // syncLink never queries at all (a blank email is filtered out before the RPC), so
    // the generic copy below would read "...for the email address ." with an empty bold
    // and blame a lookup that never happened. Email is optional on the client form, so
    // this is an ordinary state, not an error — say what to do instead.
    if (!patient?.email && !isLinked && !isAccessDenied && !isAwaitingConsent) {
        return (
        <Box
            sx={lockedCardSx}
        >
            <Box
            sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor: '#e0e0e0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
                color: '#757575'
            }}
            >
            <LinkOffIcon sx={{ fontSize: 40 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: '#4a5568' }}>
            {UNLOCK_TITLE}
            </Typography>
            <Typography variant="body1" sx={{ color: '#374151', maxWidth: '440px', mb: 1.5 }}>{UNLOCK_INTRO}</Typography>
            <Typography variant="body1" sx={{ color: '#718096', maxWidth: '400px', mb: 3 }}>
            This client has no email address on file, so there is no MindVault account to
            connect to.
            </Typography>
            <Typography variant="body2" sx={{ color: '#a0aec0' }}>
                Add the email they use for the Mindvault application in the Profile tab, and
                the dashboard will appear here once they connect.
            </Typography>
        </Box>
        );
    }

    if (!isLinked && !isAccessDenied && !isAwaitingConsent) {
        return (
        <Box
            sx={lockedCardSx}
        >
            <Box 
            sx={{ 
                width: 80, 
                height: 80, 
                borderRadius: '50%', 
                backgroundColor: '#e0e0e0', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                mb: 2,
                color: '#757575'
            }}
            >
            <LinkOffIcon sx={{ fontSize: 40 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: '#4a5568' }}>
            {UNLOCK_TITLE}
            </Typography>
            <Typography variant="body1" sx={{ color: '#374151', maxWidth: '440px', mb: 1.5 }}>{UNLOCK_INTRO}</Typography>
            <Typography variant="body1" sx={{ color: '#718096', maxWidth: '400px', mb: 3 }}>
            No Mindvault account found for the email address <strong>{patient?.email}</strong>.
            </Typography>
            <Typography variant="body2" sx={{ color: '#a0aec0' }}>
                The dashboard will appear here once the patient creates an account on the Mindvault application.
            </Typography>
        </Box>
        );
    }

    // Paid, but the client hasn't switched on sharing. Distinct from "not
    // connected": the therapist is being billed for this client and their notes
    // keep feeding the client's MindVault chatbot — only the client's own data
    // is withheld. Never offer to sell a second access code here.
    if (isAwaitingConsent) {
        const paymentPending = connectionEntitlement === ENTITLEMENT.ACCESS_CODE_PENDING;
        return (
        <Box
            sx={lockedCardSx}
        >
            <Box
            sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor: '#F0FDFA',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
                color: '#29C0BB'
            }}
            >
            <LockOutlinedIcon sx={{ fontSize: 40 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: '#4a5568' }}>
            {RESTRICTED_ACCESS_LABEL}
            </Typography>
            <Typography variant="body1" sx={{ color: '#718096', maxWidth: '440px', mb: 1.5 }}>
            You are connected to <strong>{patient?.email}</strong>
            {paymentPending
                ? ' — your access code purchase is still being confirmed.'
                : '.'}{' '}
            They have not shared their MindVault data with you yet, so their
            chats, scans and assessments stay hidden here.
            </Typography>
            <Typography variant="body2" sx={{ color: '#a0aec0', maxWidth: '440px', mb: 1 }}>
            Your consultation notes continue to guide this client's MindVault
            chatbot — nothing about the connection is on hold.
            </Typography>
            <Typography variant="body2" sx={{ color: '#a0aec0', maxWidth: '440px' }}>
            Ask the client to turn on sharing with you from their MindVault app
            and this dashboard fills in automatically. No further purchase is
            needed.
            </Typography>
        </Box>
        );
    }

    if (isAccessDenied) {
        return (
        <Box
            sx={lockedCardSx}
        >
            <Box
            sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor: '#fdecea',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
                color: '#d32f2f'
            }}
            >
            <LockOutlinedIcon sx={{ fontSize: 40 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: '#4a5568' }}>
            {isBookingLapsed ? 'Connection Lapsed' : UNLOCK_TITLE}
            </Typography>
            {isBookingLapsed ? (
              <>
                <Typography variant="body1" sx={{ color: '#718096', maxWidth: '420px', mb: 1.5 }}>
                This client's last MindVault booking with you was more than 30 days
                ago, so the free booking connection has lapsed.
                </Typography>
                <Typography variant="body2" sx={{ color: '#a0aec0', mb: 3 }}>
                Ask the client to book you again through MindVault (free for another
                30 days), or convert them to a paid connection below to reconnect
                immediately.
                </Typography>
              </>
            ) : (
              <>
                <Typography variant="body1" sx={{ color: '#374151', maxWidth: '440px', mb: 1.5 }}>{UNLOCK_INTRO}</Typography>
                <Typography variant="body1" sx={{ color: '#718096', maxWidth: '420px', mb: 1.5 }}>
                You are not connected to this client's MindVault account. A connection
                requires a paid TikCare plan (Basic or Pro), a MindVault booking with
                you, or a monthly access code — plus the client's consent to share.
                </Typography>
                <Typography variant="body2" sx={{ color: '#a0aec0', mb: 3 }}>
                After purchase, the client must also approve sharing from the
                MindVault app before data appears here.
                </Typography>
              </>
            )}
            {/* Legacy fiat access-code path — gated OFF by PHASE45_COMMS_ENABLED
                so post-flip only the credit path below shows (mirrors the
                mutually-exclusive swap in AccessCodesSection.js). */}
            {!PHASE45_COMMS_ENABLED && (
              <BuyAccessCodeButton
                therapistUserId={currentUserId}
                clientEmail={patient?.email}
                onPurchased={() => setConnectionRefreshVersion((version) => version + 1)}
              />
            )}
            {/* Phase 5 credit-purchase path (flag-gated; renders nothing
                while PHASE45_COMMS_ENABLED is off): connects this client
                with credits via the frozen purchase RPC. */}
            {PHASE45_COMMS_ENABLED && (
              <Box sx={{ mt: 1.5 }}>
                <CreditConnectionPurchase
                  clientEmail={patient?.email}
                  userId={currentUserId}
                  onPurchased={() => setConnectionRefreshVersion((version) => version + 1)}
                />
              </Box>
            )}
        </Box>
        );
    }

        return null;
    })();

    const latestRiskStyle = getRiskStyles(latestSession?.risk_level);

const AssessmentDetailDialog = ({ open, type, data, onClose }) => {
    const meta = getAssessmentMeta(type);
    // Sort data chronological for the chart: Oldest -> Newest
    const chartData = useMemo(() => {
        return [...data]
            .sort((a, b) => new Date(a.completed_at) - new Date(b.completed_at))
            .map(item => ({
                date: formatDate(item.completed_at),
                rawDate: new Date(item.completed_at),
                score: item.total_score,
                severity: item.severity_level
            }));
    }, [data]);

    const latest = chartData[chartData.length - 1];
    const previous = chartData.length > 1 ? chartData[chartData.length - 2] : null;
    const isImproved = previous && latest.score < previous.score; // Lower score usually better for PHQ/GAD

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3, p: 1 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                    <Box sx={{ 
                        width: 40, height: 40, borderRadius: '50%', backgroundColor: '#F5F3FF', 
                        display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#7C3AED' 
                    }}>
                        <AssignmentIcon />
                    </Box>
                    <Typography variant="h6" fontWeight="bold">{meta.label}</Typography>
                </Stack>
                <IconButton onClick={onClose}><CloseIcon /></IconButton>
            </Box>
            
            <DialogContent>
                <Box sx={{ mb: 4, p: 2, bgcolor: '#FAFAFA', borderRadius: 2 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                            <Typography variant="subtitle2" color="textSecondary">Latest Score</Typography>
                            <Typography variant="h4" fontWeight="bold" color="#7C3AED">{latest?.score ?? '--'}</Typography>
                        </Box>
                        {previous && (
                            <Chip 
                                icon={isImproved ? <TrendingDownIcon /> : <TrendingUpIcon />} // Down is usually good for symptoms
                                label={isImproved ? "Improving" : "Worsening"} 
                                sx={{ 
                                    bgcolor: isImproved ? '#ECFDF5' : '#FEF2F2', 
                                    color: isImproved ? '#059669' : '#DC2626',
                                    fontWeight: 'bold'
                                }} 
                            />
                        )}
                    </Stack>
                </Box>

                <Box sx={{ height: 300, width: '100%' }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartData} margin={{ top: 20, right: 30, left: 30, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis 
                                dataKey="date" 
                                tick={{fontSize: 12}} 
                                interval="preserveStartEnd" 
                            />
                            <YAxis domain={[0, meta.max]} hide />
                            <RechartsTooltip 
                                contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                            />
                            {/* Background Reference Areas for Severity */}
                            {meta.ranges.map((range, idx) => {
                                const prevMax = idx === 0 ? 0 : meta.ranges[idx - 1].max;
                                return (
                                    <ReferenceArea 
                                        key={range.name} 
                                        y1={prevMax} 
                                        y2={range.max} 
                                        fill={range.color} 
                                        fillOpacity={0.05} 
                                    />
                                );
                            })}
                            <Line 
                                type="monotone" 
                                dataKey="score" 
                                stroke="#7C3AED" 
                                strokeWidth={3} 
                                dot={{ fill: '#7C3AED', r: 4, strokeWidth: 2, stroke: '#fff' }} 
                                activeDot={{ r: 6 }} 
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </Box>
                
                {/* Severity Legend */}
                <Stack direction="row" spacing={2} justifyContent="center" mt={2}>
                    {meta.ranges.map(range => (
                        <Stack key={range.name} direction="row" spacing={0.5} alignItems="center">
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: range.color }} />
                            <Typography variant="caption" color="textSecondary">{range.name}</Typography>
                        </Stack>
                    ))}
                </Stack>

                {/* Every result with its date, newest first */}
                <Box component="table" sx={{ width: '100%', mt: 3, borderCollapse: 'collapse', fontSize: 14, '& th, & td': { textAlign: 'left', py: 1, px: 1.5, borderTop: '1px solid #E3E9EE' }, '& th': { fontSize: 12, fontWeight: 600, color: '#5B6675', textTransform: 'uppercase', bgcolor: '#F7FAFB' } }}>
                    <thead><tr><th scope="col">Date</th><th scope="col">Score</th><th scope="col">Severity</th><th scope="col">Change</th></tr></thead>
                    <tbody>
                        {chartData.map((row, index) => {
                            const prev = index > 0 ? chartData[index - 1].score : null;
                            const diff = prev === null || row.score === null || row.score === undefined ? null : row.score - prev;
                            return { ...row, change: diff === null ? '—' : diff > 0 ? `+${diff}` : diff < 0 ? `−${Math.abs(diff)}` : '0' };
                        }).reverse().map((row, index) => (
                            <tr key={`${row.date}-${index}`}>
                                <td>{row.date}</td>
                                <td style={{ fontWeight: 600 }}>{row.score ?? '--'}{meta.max ? ` / ${meta.max}` : ''}</td>
                                <td>{row.severity || '—'}</td>
                                <td style={{ color: '#5B6675' }}>{row.change}</td>
                            </tr>
                        ))}
                    </tbody>
                </Box>
            </DialogContent>
        </Dialog>
        );
    };


    return (
    <Box sx={{ p: 3, pl: 0 }}>
        <ConsultationOverviewSection patient={patient} aside={
            <MindVaultAlertsBanner connected={!lockedState} latestSession={latestSession} latestRiskSignals={latestRiskSignals} latestRiskStyle={latestRiskStyle} vitalSummary={vitalSummary} />
        } />

        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#14234B', whiteSpace: 'nowrap' }}>From the client's MindVault</Typography>
            <Divider sx={{ flex: 1 }} />
        </Stack>

        {lockedState ? (
        <Box sx={{ position: 'relative', minHeight: 480, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2, overflow: 'hidden', borderRadius: 2 }}>
            <Box sx={{ position: 'absolute', inset: 0, filter: 'blur(6px)', pointerEvents: 'none', userSelect: 'none' }}>
                <LockedMindVaultPreview />
            </Box>
            <Box sx={{ position: 'relative', width: '100%', display: 'flex', justifyContent: 'center' }}>
                {lockedState}
            </Box>
        </Box>
        ) : (<>
        {/* Latest Session Analysis */}

        {/* Chat Summaries + Session Transcript */}
        <Grid container spacing={2} alignItems="stretch">
            <Grid size={{ xs: 12, md: 6 }}>
                <ChatSummaryCard chatCustomFrom={chatCustomFrom} chatCustomTo={chatCustomTo} chatRange={chatRange} chatSummariesRef={chatSummariesRef} filteredChatSessions={filteredChatSessions} formatDate={formatDate} getRiskStyles={getRiskStyles} loadingConversation={loadingConversation} openSession={openSession} openTranscriptSessionId={openTranscriptSessionId} selectedConversation={selectedConversation} selectedSessionId={selectedSessionId} setChatAppliedFrom={setChatAppliedFrom} setChatAppliedTo={setChatAppliedTo} setChatCustomFrom={setChatCustomFrom} setChatCustomTo={setChatCustomTo} setChatRange={setChatRange} setOpenTranscriptSessionId={setOpenTranscriptSessionId} setSelectedSessionId={setSelectedSessionId} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
                <DashboardAssessmentCards compact assessmentGroups={assessmentGroups} assessmentsLoading={assessmentsLoading} assessmentsRef={assessmentsRef} formatDate={formatDate} getAssessmentMeta={getAssessmentMeta} getSeverityColor={getSeverityColor} setSelectedAssessmentType={setSelectedAssessmentType} />
            </Grid>
        </Grid>

        {/* Wellness Overview */}
        <Box ref={wellnessRef} sx={{ mt: 4 }}>
            <WellnessOverviewSection
                mode={isCorporateVitalsMode ? 'Corporates' : 'MindVault'}
                userId={mindvaultUserId}
                currentOrgId={currentOrg?.id}
                record={record}
                toNumOrNull={toNumOrNull}
                getHrStatus={getHrStatus}
                getSpo2Status={getSpo2Status}
                getBpStatus={getBpStatus}
                getHrvStatus={getHrvStatus}
                getSiStatus={getSiStatus}
                getRrStatus={getRrStatus}
                scoreLabel={scoreLabel}
                clampScore={clampScore}
            />
        </Box>

        {/* Mental Health Assessments */}
        {selectedAssessmentType && (
            <AssessmentDetailDialog 
                open={!!selectedAssessmentType}
                type={selectedAssessmentType}
                data={assessmentGroups[selectedAssessmentType] || []}
                onClose={() => setSelectedAssessmentType(null)}
            />
        )}

        {/* <Box ref={timelineRef} sx={{ mt: 4 }}>
        <Paper
            elevation={0}
            sx={{
            p: 3,
            borderRadius: 2,
            border: '1px solid #e6e6e6',
            backgroundColor: '#ffffff',
            }}
        >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <TimelineIcon sx={{ color: '#29C0BB' }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Timeline
            </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
            Timeline details will appear here.
            </Typography>
        </Paper>
        </Box> */}
        </>)}
    </Box>
    );
    };

    export default VitalsDashboard;
